import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Tag, Modal, Form, Input, Select,
  message, Tabs, Space, Drawer, Upload,
} from 'antd';
import { PlusOutlined, MessageOutlined, UploadOutlined } from '@ant-design/icons';
import { buyerService } from '../../services/api';
import TicketHilo from '../../components/TicketHilo';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { Option } = Select;

const estadoColor    = { abierto: 'blue', en_proceso: 'orange', resuelto: 'green', cerrado: 'default' };
const prioridadColor = { baja: 'default', normal: 'blue', alta: 'orange', urgente: 'red' };

const TIPOS      = ['consulta', 'reclamo', 'correccion', 'garantia', 'soporte_tecnico'];
const ESTADOS    = ['abierto', 'en_proceso', 'resuelto', 'cerrado'];
const PRIORIDADES = ['baja', 'normal', 'alta', 'urgente'];

// canal backend → key de tab
const TAB_CANAL = {
  entrada:  'recibidos',
  enviados: 'enviados_comprador',
  puja:     'enviados_puja',
};

const TicketsAdmin = () => {
  const [tabActiva, setTabActiva] = useState('entrada');
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState([]);

  // Drawer detalle
  const [ticketActivo, setTicketActivo] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [mensajeText, setMensajeText] = useState('');
  const [enviando, setEnviando] = useState(false);

  // Modal único "Nuevo mensaje"
  const [modalNuevo, setModalNuevo] = useState(false);
  const [formNuevo] = Form.useForm();
  const [destinatarioTipo, setDestinatarioTipo] = useState('comprador');
  const [compradores, setCompradores] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [creando, setCreando] = useState(false);

  const fetchTickets = useCallback(async (tab) => {
    setLoading(true);
    try {
      const res = await buyerService.getTicketsAdmin(TAB_CANAL[tab]);
      setTickets(res.data || []);
    } catch (err) {
      message.error(err.response?.data?.error || 'Error al cargar tickets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTickets(tabActiva);
  }, [tabActiva, fetchTickets]);

  const fetchCompradores = useCallback(async () => {
    try {
      const res = await buyerService.getCompradores();
      setCompradores(res.data || []);
    } catch { /* silencioso */ }
  }, []);

  const abrirNuevo = () => {
    fetchCompradores();
    setDestinatarioTipo('comprador');
    formNuevo.resetFields();
    formNuevo.setFieldsValue({ destinatario_tipo: 'comprador', prioridad: 'normal' });
    setModalNuevo(true);
  };

  const cerrarNuevo = () => {
    setModalNuevo(false);
    formNuevo.resetFields();
    setFileList([]);
  };

  const abrirDetalle = async (id) => {
    setLoadingDetalle(true);
    try {
      const res = await buyerService.getTicket(id);
      setTicketActivo(res.data);
      setMensajeText('');
    } catch (err) {
      message.error(err.response?.data?.error || 'Error al cargar ticket');
    } finally {
      setLoadingDetalle(false);
    }
  };

  const enviarMensaje = async () => {
    if (!mensajeText.trim() || !ticketActivo) return;
    setEnviando(true);
    try {
      await buyerService.responderTicketAdmin(ticketActivo.id_ticket, mensajeText);
      const res = await buyerService.getTicket(ticketActivo.id_ticket);
      setTicketActivo(res.data);
      setMensajeText('');
      fetchTickets(tabActiva);
    } catch (err) {
      message.error(err.response?.data?.error || 'Error al enviar mensaje');
    } finally {
      setEnviando(false);
    }
  };

  const cambiarEstado = async (nuevoEstado) => {
    if (!ticketActivo) return;
    try {
      await buyerService.actualizarTicket(ticketActivo.id_ticket, { estado: nuevoEstado });
      setTicketActivo(prev => ({ ...prev, estado: nuevoEstado }));
      fetchTickets(tabActiva);
      message.success('Estado actualizado');
    } catch (err) {
      message.error(err.response?.data?.error || 'Error al actualizar estado');
    }
  };

  const crearMensaje = async (values) => {
    setCreando(true);
    try {
      let res;
      if (values.destinatario_tipo === 'puja') {
        res = await buyerService.crearTicketPuja(values);
        message.success('Ticket enviado a Servicio Técnico Puja');
      } else {
        res = await buyerService.crearTicketComprador(values);
        message.success('Mensaje enviado al comprador');
      }

      if (fileList.length > 0) {
        try {
          await buyerService.subirImagenesTicket(res.data.id_ticket, fileList);
        } catch {
          message.warning('Mensaje creado pero no se pudieron subir algunas imágenes');
        }
      }

      cerrarNuevo();
      fetchTickets(tabActiva);
    } catch (err) {
      message.error(err.response?.data?.error || 'Error al enviar mensaje');
    } finally {
      setCreando(false);
    }
  };

  const columnas = [
    {
      title: 'Asunto',
      dataIndex: 'asunto',
      width: 300,
      ellipsis: true,
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      width: 120,
      render: (v) => <Tag>{v}</Tag>,
    },
    ...(tabActiva === 'entrada' ? [{
      title: 'Remitente',
      key: 'remitente',
      width: 160,
      render: (_, r) => r.usuario?.nom_usuario || `#${r.id_usuario}`,
    }] : []),
    ...(tabActiva === 'enviados' ? [{
      title: 'Destinatario',
      key: 'destinatario',
      width: 160,
      render: (_, r) => r.usuario_destino?.nom_usuario || `#${r.id_usuario_destino}`,
    }] : []),
    {
      title: 'Prioridad',
      dataIndex: 'prioridad',
      width: 100,
      render: (v) => <Tag color={prioridadColor[v] || 'blue'}>{v || 'normal'}</Tag>,
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      width: 110,
      render: (v) => <Tag color={estadoColor[v] || 'default'}>{v}</Tag>,
    },
    {
      title: 'Actualizado',
      dataIndex: 'updated_at',
      width: 130,
      render: (v) => dayjs(v).format('DD/MM/YY HH:mm'),
    },
    {
      title: '',
      key: 'accion',
      width: 70,
      render: (_, r) => (
        <Button
          size="small"
          icon={<MessageOutlined />}
          onClick={() => abrirDetalle(r.id_ticket)}
        >
          Ver
        </Button>
      ),
    },
  ];

  const tablaComun = (
    <Table
      scroll={{ x: 'max-content' }}
      dataSource={tickets}
      columns={columnas}
      rowKey="id_ticket"
      loading={loading}
      pagination={{ pageSize: 10 }}
      size="small"
    />
  );

  const tabItems = [
    {
      key: 'entrada',
      label: 'Bandeja de entrada',
      children: tablaComun,
    },
    {
      key: 'enviados',
      label: 'Enviados',
      children: tablaComun,
    },
    {
      key: 'puja',
      label: 'Servicio Técnico Puja',
      children: tablaComun,
    },
  ];

  return (
    <div>
      {/* Header de sección */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ margin: 0 }}>Consultas y Reclamos</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={abrirNuevo}>
          Nuevo mensaje
        </Button>
      </div>

      <Tabs
        activeKey={tabActiva}
        onChange={setTabActiva}
        items={tabItems}
      />

      {/* Drawer: detalle del ticket */}
      <Drawer
        title={ticketActivo?.asunto || 'Detalle'}
        open={!!ticketActivo}
        onClose={() => setTicketActivo(null)}
        width={560}
        loading={loadingDetalle}
        extra={
          ticketActivo && (
            <Space>
              <Select
                value={ticketActivo.estado}
                onChange={cambiarEstado}
                style={{ width: 130 }}
                size="small"
              >
                {ESTADOS.map(e => <Option key={e} value={e}>{e}</Option>)}
              </Select>
            </Space>
          )
        }
      >
        {ticketActivo && (
          <TicketHilo
            ticket={ticketActivo}
            mensajeText={mensajeText}
            onMensajeChange={setMensajeText}
            onEnviar={enviarMensaje}
            enviando={enviando}
          />
        )}
      </Drawer>

      {/* Modal único: nuevo mensaje */}
      <Modal
        title="Nuevo mensaje"
        open={modalNuevo}
        onCancel={cerrarNuevo}
        footer={null}
        destroyOnHidden
      >
        <Form form={formNuevo} layout="vertical" onFinish={crearMensaje}>
          <Form.Item name="destinatario_tipo" label="Enviar a" initialValue="comprador">
            <Select
              onChange={(v) => {
                setDestinatarioTipo(v);
                formNuevo.setFieldValue('id_usuario_destino', undefined);
              }}
            >
              <Option value="comprador">Un comprador</Option>
              <Option value="puja">Servicio Técnico Puja</Option>
            </Select>
          </Form.Item>

          {destinatarioTipo === 'comprador' && (
            <Form.Item
              name="id_usuario_destino"
              label="Comprador"
              rules={[{ required: true, message: 'Seleccioná un comprador' }]}
            >
              <Select
                showSearch
                optionFilterProp="children"
                placeholder={compradores.length === 0 ? 'Sin compradores vinculados' : 'Seleccioná un comprador'}
              >
                {compradores.map((c) => (
                  <Option key={c.id_usuario} value={c.id_usuario}>
                    {c.nom_usuario} — {c.email_usuario}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item name="tipo" label="Tipo" rules={[{ required: true, message: 'Requerido' }]}>
            <Select placeholder="Tipo de mensaje">
              {TIPOS.map(t => <Option key={t} value={t}>{t}</Option>)}
            </Select>
          </Form.Item>

          <Form.Item name="prioridad" label="Prioridad" initialValue="normal">
            <Select>
              {PRIORIDADES.map(p => (
                <Option key={p} value={p}><Tag color={prioridadColor[p]}>{p}</Tag></Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="asunto" label="Asunto" rules={[{ required: true, message: 'Requerido' }]}>
            <Input placeholder="Asunto del mensaje" />
          </Form.Item>

          <Form.Item name="descripcion" label="Descripción">
            <TextArea rows={4} placeholder="Escribí el mensaje..." />
          </Form.Item>

          <Form.Item label="Imágenes adjuntas (opcional)">
            <Upload
              beforeUpload={() => false}
              fileList={fileList}
              onChange={({ fileList: fl }) => setFileList(fl)}
              accept="image/png,image/jpeg,image/gif,image/webp"
              multiple
              listType="picture"
            >
              <Button icon={<UploadOutlined />}>Seleccionar imágenes</Button>
            </Upload>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={creando} block>
              Enviar
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TicketsAdmin;
